export class SyncError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "SyncError";
  }
}

// --- cold-start resilience -------------------------------------------------
// The hosted backend (Render free tier) spins down when idle; the first request
// after that can take ~30-90s (boot + migrations + DB connect) and may bounce a
// 502/503 while the gateway has no app to route to. We retry those transient
// failures with backoff and emit a "waking" signal so the UI can reassure the
// user instead of looking dead. 4xx (incl. 401) and a normal slow-but-served
// request are NOT retried — only gateway errors and outright network failures.

const TRANSIENT_STATUS = new Set([502, 503, 504]);
/** Backoff between retry attempts (ms). Length = max retries (~30s of waiting,
 *  on top of however long each hung attempt itself takes). */
const RETRY_DELAYS_MS = [1500, 3000, 5000, 8000, 12000];
/** A request still pending after this long is treated as "the server is waking". */
const SLOW_AFTER_MS = 4000;

type WakingListener = (waking: boolean) => void;
let wakingListeners: WakingListener[] = [];
let slowCount = 0;

/** Subscribe to the cold-start signal: `waking` flips true while one or more
 *  requests are slow/retrying and false once they all settle. Returns an
 *  unsubscribe. The web app uses this to show a "server waking up" notice. */
export function onServerWaking(fn: WakingListener): () => void {
  wakingListeners.push(fn);
  return () => {
    wakingListeners = wakingListeners.filter((l) => l !== fn);
  };
}

function setSlow(delta: 1 | -1): void {
  const wasActive = slowCount > 0;
  slowCount = Math.max(0, slowCount + delta);
  const isActive = slowCount > 0;
  if (wasActive !== isActive) for (const l of wakingListeners) l(isActive);
}

const delay = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** Run a fetch with transient-failure retry + the waking signal. Returns the
 *  final `Response` (which may still be a non-2xx the caller must handle). */
async function fetchWithRetry(doFetch: () => Promise<Response>): Promise<Response> {
  let markedSlow = false;
  const markSlow = () => {
    if (!markedSlow) {
      markedSlow = true;
      setSlow(1);
    }
  };
  const slowTimer = setTimeout(markSlow, SLOW_AFTER_MS);
  try {
    for (let attempt = 0; ; attempt++) {
      try {
        const res = await doFetch();
        if (TRANSIENT_STATUS.has(res.status) && attempt < RETRY_DELAYS_MS.length) {
          markSlow(); // a bounced gateway is a strong cold-start tell
          await delay(RETRY_DELAYS_MS[attempt]!);
          continue;
        }
        return res;
      } catch (e) {
        if (attempt < RETRY_DELAYS_MS.length) {
          markSlow();
          await delay(RETRY_DELAYS_MS[attempt]!);
          continue;
        }
        throw e;
      }
    }
  } finally {
    clearTimeout(slowTimer);
    if (markedSlow) setSlow(-1);
  }
}

async function readError(res: Response): Promise<SyncError> {
  const detail = await res.text().catch(() => "");
  return new SyncError(parseError(detail) || `Server returned ${res.status}`, res.status);
}

/** POST JSON to the server and parse the JSON response. Throws {@link SyncError}
 *  (carrying the HTTP status) on a non-2xx reply or an unreachable server. Shared
 *  by the auth and sync clients. A 204 (no content) resolves to `undefined`.
 *  Transient gateway/network failures are retried (see cold-start note above). */
export async function postJson<T>(
  url: string,
  body: unknown,
  headers: Record<string, string> = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetchWithRetry(() =>
      fetch(url, {
        method: "POST",
        headers: { "content-type": "application/json", ...headers },
        body: JSON.stringify(body),
      }),
    );
  } catch (e) {
    throw new SyncError(`Can't reach the server: ${(e as Error).message}`);
  }
  if (!res.ok) throw await readError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** GET JSON with a bearer token. */
export async function getJson<T>(url: string, accessToken: string): Promise<T> {
  let res: Response;
  try {
    res = await fetchWithRetry(() => fetch(url, { headers: { authorization: `Bearer ${accessToken}` } }));
  } catch (e) {
    throw new SyncError(`Can't reach the server: ${(e as Error).message}`);
  }
  if (!res.ok) throw await readError(res);
  return (await res.json()) as T;
}

/** DELETE with a bearer token. A 204 (no content) resolves to `undefined`. */
export async function delJson<T>(url: string, accessToken: string): Promise<T> {
  let res: Response;
  try {
    res = await fetchWithRetry(() =>
      fetch(url, { method: "DELETE", headers: { authorization: `Bearer ${accessToken}` } }),
    );
  } catch (e) {
    throw new SyncError(`Can't reach the server: ${(e as Error).message}`);
  }
  if (!res.ok) throw await readError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Our server returns `{ error: "..." }`; surface that message when present. */
function parseError(detail: string): string {
  try {
    const obj = JSON.parse(detail) as { error?: unknown; message?: unknown };
    if (typeof obj.error === "string") return obj.error;
    if (typeof obj.message === "string") return obj.message;
  } catch {
    /* not JSON */
  }
  return detail;
}

export const trimBase = (baseUrl: string): string => baseUrl.replace(/\/$/, "");
